import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { LocalDatePipe } from '../../../../../shared/pipes/local-date.pipe';
import { ShortDatePipe } from '../../../../../shared/pipes/short-date.pipe';
import { FileLinkComponent } from '../../../../../shared/components/kit/file-link/file-link.component';
import { FileLinkInput } from '../../../../../shared/components/kit/file-link/file-link.types';
import { contentGlobalSuccess } from '../../../../content.error';
import { routeParams } from '../../../../../shared/shared.global-service';
import { MessageRecipientDetailPageStore } from './message-recipient-detail-page.store';
import { messageRecipientDetailFrag } from './message-recipient-detail-page.query';

@Component({
    selector: 'app-message-recipient-detail-page',
    templateUrl: './message-recipient-detail-page.component.html',
    styleUrl: './message-recipient-detail-page.component.scss',
    providers: [MessageRecipientDetailPageStore],
    imports: [
        TranslatePipe,
        CardComponent,
        ButtonComponent,
        LocalDatePipe,
        ShortDatePipe,
        FileLinkComponent,
    ],
})
export class MessageRecipientDetailPageComponent {

    public readonly store = inject(MessageRecipientDetailPageStore);

    public id = input<number | null>(null);

    protected readonly prefix = 'messages.';

    public readonly recipient = signal<any>(null);

    protected readonly isRead = computed<boolean>(() => !!this.recipient()?.readAt);
    protected readonly saving = signal<boolean>(false);

    protected readonly files = computed<FileLinkInput[]>(() =>
        (this.recipient()?.message?.files ?? []).map((it: any) => ({
            fileId: it.id,
            name: it.name,
            path: it.path,
            type: null,
        })),
    );

    constructor() {
        effect(() => {
            const inputId = this.id();
            if (inputId) {
                this.loadDetail(inputId);
                return;
            }
            const routeId = routeParams.id();
            if (routeId && routeId !== '0') {
                this.loadDetail(Number(routeId));
            }
        });
    }

    public async confirmRead(): Promise<void> {
        const r = this.recipient();
        if (!r?.id || this.isRead() || this.saving()) {
            return;
        }
        this.saving.set(true);
        const res = await this.store.api.markMessageRecipientAsRead({ id: r.id });
        this.saving.set(false);
        if (res?.ok) {
            this.recipient.update((current) => ({ ...current, readAt: new Date().toISOString() }));
            contentGlobalSuccess.next('common.updated');
        }
    }

    private async loadDetail(id: number): Promise<void> {
        const res = await this.store.api.getMyMessageRecipients(
            FilterBuilder.filterById(id),
            messageRecipientDetailFrag,
        );
        this.recipient.set(res?.items?.[0] ?? null);
    }
}
